import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useMemo, useState } from 'react';
import makeCancelable, { CanceledError } from 'core/util/cancelablePromise';
import { inject } from 'mobx-react';
import hoistNonReactStatics from 'hoist-non-react-statics';

/**
 * Maps store names to their actual types.
 * Add new store types here as they become available.
 */

export default function storeLoader(...collections) {
  return Component => {
    const Wrapper = inject('$store')(({
      $store,
      ...props
    }) => {
      const [loading, setLoading] = useState(true);
      const stores = useMemo(() => collections.map(collection => {
        let force = false;
        let collectionStr;
        if (typeof collection === 'string') {
          collectionStr = collection;
        } else {
          force = collection.force ?? false;
          collectionStr = collection.store;
        }
        const [storeName, collectionName = 'collection'] = collectionStr.split('.');
        return {
          store: $store[storeName],
          storeName,
          collection: $store[storeName]?.[collectionName],
          force
        };
      }), [$store]);
      const [request, setRequest] = useState(null);
      useEffect(() => {
        setRequest(makeCancelable(Promise.all(stores.map(({
          collection,
          force
        }) => {
          const fetchable = collection;
          return fetchable?.fetch?.({
            force
          });
        }))));
      }, [stores]);
      useEffect(() => {
        request?.promise.then(() => {
          setRequest(null);
          setLoading(false);
        }).catch(err => {
          if (!(err instanceof CanceledError)) {
            setLoading(false);
            console.error('Error loading required resource', err);
          }
        });
        return () => {
          if (request) {
            request.cancel();
          }
        };
      }, [request]);
      return /*#__PURE__*/React.createElement(Component, _extends({}, props, {
        $store,
        loading
      }, stores.reduce((acc, {
        storeName,
        store
      }) => {
        acc[storeName] = store;
        return acc;
      }, {})));
    });
    hoistNonReactStatics(Wrapper, Component);
    return Wrapper;
  };
}